import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { trigger, style, transition, animate } from '@angular/animations';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { CheckboxComponent } from '@components/checkbox';
import { ButtonComponent } from '@components/button';
import { InputComponent } from '@components/input';
import { DatePickerComponent } from '@components/date-picker';
import { DropdownComponent, DropdownOption } from '@components/dropdown/dropdown.component';
import { ThemeService } from '@services/theme.service';
import { DialogComponent } from '@components/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { OpenlayersMapComponent } from '@components/openlayers-map/openlayers-map.component';
import { TextareaComponent } from '@components/textarea';
import { TypographyComponent } from "@components/typography";
import { LeafletMapComponent } from '@components/leaflet-map/leaflet-map.component';
import { MaplibreGlComponent } from '@components/maplibre-gl/maplibre-gl.component';
import { environment } from '@root/src/environments/environment';

interface DemoUser {
  id: number;
  name: string;
  email: string;
  role: string;
  department: string;
  status: 'active' | 'inactive' | 'blocked';
  lastLogin: Date;
  selected?: boolean;
}

@Component({
  selector: 'app-data-table-demo',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    TooltipModule,
    RouterLink,
    FormsModule,
    DataTableComponent,
    CheckboxComponent,
    ButtonComponent,
    InputComponent,
    DatePickerComponent,
    DropdownComponent,
     DialogComponent,
    InputTextModule,
     OpenlayersMapComponent,
    TextareaComponent,
    TypographyComponent,
    LeafletMapComponent,
    MaplibreGlComponent
  ],
  templateUrl: './data-table-demo.component.html',
  styleUrls: ['./data-table-demo.component.scss', './dialog-styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('dropdownAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ])
  ]
})
export class DataTableDemoComponent implements OnInit, OnDestroy {
  @ViewChild('maplibreMap') maplibreMap: MaplibreGlComponent | undefined;
  @ViewChild('demoMap') demoMap: MaplibreGlComponent | undefined;
  @ViewChild('leafletMapDemo', { read: LeafletMapComponent }) leafletMap: LeafletMapComponent | undefined;
  @ViewChild('openlayersMapDemo', { read: OpenlayersMapComponent }) openlayersMap: OpenlayersMapComponent | undefined;
  private destroy$ = new Subject<void>();
  users: DemoUser[] = [];
  totalRecords = 0;
  loading = false;

  environment = environment;

  demoSatelliteOpacity = 50;
  demoLayersSwapped = false;
  satelliteLayerVisible = true;

  leafletSatelliteVisible = false;
  leafletSatelliteOpacity = 50;
  openlayersSatelliteVisible = false;
  openlayersSatelliteOpacity = 50;

  leafletDynamicPolygonsEnabled = false;
  leafletLoadedPolygons = new Map<string, any>();
  leafletLoadedBounds = new Set<string>();
  leafletPolygonsCount = 0;
  private leafletMoveSubject = new Subject<any>();

  openlayersDynamicPolygonsEnabled = false;
  openlayersLoadedPolygons = new Map<string, any>();
  openlayersLoadedBounds = new Set<string>();
  openlayersPolygonsCount = 0;
  private openlayersMoveSubject = new Subject<any>();

  dynamicPolygonsEnabled = false;
  loadedPolygons = new Map<string, any>();
  loadedBounds = new Set<string>();
  polygonsCount = 0;
  private moveSubject = new Subject<any>();

  checkboxStates = {
    demo: false
  };

  inputValue: string = '';
  textareaValue: string = '';
  selectedDate: Date | null = null;
  selectedDateTime: Date | null = null;
  selectedDateRange: Date[] | null = null;

  selectedDropdownValue: any = null;
  dropdownOptions: DropdownOption[] = [
    { label: 'Option 1', value: 1 },
    { label: 'Option 2', value: 2 },
    { label: 'Option 3', value: 3 },
    { label: 'Option 4', value: 4 },
    { label: 'Option 5', value: 5 },
    { label: 'Option 6', value: 6 },
    { label: 'Option 7', value: 7 },
    { label: 'Option 8', value: 8 }
  ];

  selectedCountry: any = null;
  selectedCity: any = null;

  countries: DropdownOption[] = [
    { label: 'USA', value: 'usa' },
    { label: 'Canada', value: 'canada' },
    { label: 'UK', value: 'uk' },
    { label: 'Germany', value: 'germany' },
    { label: 'France', value: 'france' }
  ];

  citiesByCountry: { [key: string]: DropdownOption[] } = {
    usa: [
      { label: 'New York', value: 'new-york' },
      { label: 'Los Angeles', value: 'los-angeles' },
      { label: 'Chicago', value: 'chicago' },
      { label: 'Houston', value: 'houston' }
    ],
    canada: [
      { label: 'Toronto', value: 'toronto' },
      { label: 'Vancouver', value: 'vancouver' },
      { label: 'Montreal', value: 'montreal' },
      { label: 'Calgary', value: 'calgary' }
    ],
    uk: [
      { label: 'London', value: 'london' },
      { label: 'Manchester', value: 'manchester' },
      { label: 'Birmingham', value: 'birmingham' },
      { label: 'Edinburgh', value: 'edinburgh' }
    ],
    germany: [
      { label: 'Berlin', value: 'berlin' },
      { label: 'Munich', value: 'munich' },
      { label: 'Hamburg', value: 'hamburg' },
      { label: 'Frankfurt', value: 'frankfurt' }
    ],
    france: [
      { label: 'Paris', value: 'paris' },
      { label: 'Lyon', value: 'lyon' },
      { label: 'Marseille', value: 'marseille' },
      { label: 'Nice', value: 'nice' }
    ]
  };

  cities: DropdownOption[] = [];

  locationTreeData: any[] = [];
  isLocationDropdownOpen: boolean = false;
  selectedLocation: { country?: string; city?: string; label?: string; value?: string } = {};
  locationSearchText: string = '';
  filteredLocationData: any[] = [];
  locationOptionsForDropdown: DropdownOption[] = [];

  showBasicDialog: boolean = false;
  showModalDialog: boolean = false;
  showConfirmDialog: boolean = false;
  showFormDialog: boolean = false;

  dialogFormData = {
    name: '',
    email: '',
    role: null,
    description: ''
  };

  roleOptions = [
    { label: 'Admin', value: 'admin' },
    { label: 'Manager', value: 'manager' },
    { label: 'User', value: 'user' },
    { label: 'Guest', value: 'guest' }
  ];
  mapZoom = 13;
  mapHeight = '400px';

  leafletMarkers: any[] = [
    { id: '1', lat: 41.2995, lng: 69.2401, title: 'Tashkent Center', popup: 'Welcome to Tashkent!' },
    { id: '2', lat: 41.3111, lng: 69.2797, title: 'Chorsu Bazaar', popup: 'Famous traditional market' },
    { id: '3', lat: 41.2858, lng: 69.2035, title: 'Tashkent Tower', popup: 'TV Tower and observation deck' }
  ];
  leafletClickedCoords: [number, number] | null = null;

  maplibreMarkers: any[] = [
    {
      id: '1',
      lng: 69.2401,
      lat: 41.2995,
      popup: 'Tashkent Center - Administrative District',
      color: '#FF5722',
      draggable: false
    },
    {
      id: '2',
      lng: 69.2797,
      lat: 41.3111,
      popup: 'Chorsu Bazaar - Traditional Market',
      color: '#4CAF50',
      draggable: false
    },
    {
      id: '3',
      lng: 69.2035,
      lat: 41.2858,
      popup: 'Tashkent Tower - Landmark',
      color: '#2196F3',
      draggable: false
    }
  ];
  maplibreClickedCoords: { lng: number; lat: number } | null = null;

   tableColumns: TableColumn[] = [
    { field: 'id', header: 'ID', width: '80px', sortable: true },
    { field: 'name', header: 'Name', sortable: false },
    { field: 'email', header: 'Email', sortable: false },
    {
      field: 'role',
      header: 'Role',
      type: 'badge',
      width: '150px',
      badgeClass: (role: string) => {
        const roleClasses: Record<string, string> = {
          'Admin': 'badge-danger',
          'Manager': 'badge-warning',
          'User': 'badge-info',
          'Guest': 'badge-success'
        };
        return roleClasses[role] || 'badge-info';
      }
    },
    { field: 'department', header: 'Department', sortable: false },
    {
      field: 'status',
      header: 'Status',
      type: 'badge',
      width: '120px',
      badgeClass: (status: string) => {
        const statusClasses: Record<string, string> = {
          'active': 'badge-success',
          'inactive': 'badge-warning',
          'blocked': 'badge-danger'
        };
        return statusClasses[status] || 'badge-info';
      }
    },
    { field: 'lastLogin', header: 'Last Login', type: 'date', sortable: false },
    {
      field: 'actions',
      header: 'Actions',
      type: 'actions',
      width: '120px'
    }
  ];

  tableConfig: TableConfig = {
    selectable: true,
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [10, 25, 50, 100],
    loading: false,
    emptyMessage: 'No data found',
    actions: [
      {
        icon: 'pi pi-eye',
        tooltip: 'View Details',
        command: (row) => this.viewUser(row),
        class: 'p-button-text p-button-sm p-button-rounded'
      },
      {
        icon: 'pi pi-pencil',
        tooltip: 'Edit',
        command: (row) => this.editUser(row),
        class: 'p-button-text p-button-sm p-button-rounded'
      },
      {
        icon: 'pi pi-trash',
        tooltip: 'Delete',
        command: (row) => this.deleteUser(row),
        visible: (row) => row.status !== 'blocked',
        class: 'p-button-text p-button-sm p-button-danger p-button-rounded'
      }
    ]
  };

  constructor(
    private cdr: ChangeDetectorRef,
    public themeService: ThemeService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.generateDemoData();
    this.initializeLocationTree();

    this.moveSubject
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.dynamicPolygonsEnabled) {
          this.loadPolygonsForCurrentView();
        }
      });

    this.leafletMoveSubject
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.leafletDynamicPolygonsEnabled) {
          this.loadPolygonsForLeafletView();
        }
      });

    this.openlayersMoveSubject
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.openlayersDynamicPolygonsEnabled) {
          this.loadPolygonsForOpenlayersView();
        }
      });

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.detectChanges();
      });
  }

  generateDemoData(): void {
    const names = ['John Doe', 'Jane Smith', 'Bob Johnson', 'Alice Brown', 'Charlie Davis',
                   'Eva Wilson', 'Frank Miller', 'Grace Lee', 'Henry Taylor', 'Iris Anderson'];
    const roles = ['Admin', 'Manager', 'User', 'Guest'];
    const departments = ['IT', 'HR', 'Sales', 'Marketing', 'Finance', 'Operations'];
    const statuses: ('active' | 'inactive' | 'blocked')[] = ['active', 'inactive', 'blocked'];

    for (let i = 1; i <= 150; i++) {
      const randomName = names[Math.floor(Math.random() * names.length)];
      const randomRole = roles[Math.floor(Math.random() * roles.length)];
      const randomDept = departments[Math.floor(Math.random() * departments.length)];
      const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
      const randomDate = new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000);

      this.users.push({
        id: i,
        name: `${randomName} ${i}`,
        email: `${randomName.toLowerCase().replace(' ', '.')}${i}@example.com`,
        role: randomRole,
        department: randomDept,
        status: randomStatus,
        lastLogin: randomDate,
        selected: false
      });
    }

    this.totalRecords = this.users.length;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  onRowSelect(user: DemoUser): void {
  }

  onRowUnselect(user: DemoUser): void {
  }

  onPageChange(event: any): void {
    this.simulateLoading();
  }

  onSort(event: any): void {
    this.simulateLoading();
  }

  onFilter(event: any): void {
  }

  onSelectAllChange(selected: boolean): void {
    this.users.forEach(user => user.selected = selected);
  }

  viewUser(user: DemoUser): void {
    alert(`Viewing details for: ${user.name}`);
  }

  editUser(user: DemoUser): void {
    alert(`Editing: ${user.name}`);
  }

  deleteUser(user: DemoUser): void {
    if (confirm(`Are you sure you want to delete ${user.name}?`)) {
      this.users = this.users.filter(u => u.id !== user.id);
      this.totalRecords = this.users.length;
      this.cdr.detectChanges();
    }
  }

  addNewUser(): void {
    const newId = Math.max(...this.users.map(u => u.id)) + 1;
    const newUser: DemoUser = {
      id: newId,
      name: `New User ${newId}`,
      email: `newuser${newId}@example.com`,
      role: 'User',
      department: 'IT',
      status: 'active',
      lastLogin: new Date(),
      selected: false
    };

    this.users.unshift(newUser);
    this.totalRecords = this.users.length;
    this.cdr.detectChanges();
  }

  exportData(): void {
    alert('Export functionality would be implemented here');
  }

  refreshData(): void {
    this.simulateLoading();
    setTimeout(() => {
      this.generateDemoData();
      this.loading = false;
      this.cdr.detectChanges();
    }, 1000);
  }

  private simulateLoading(): void {
    this.loading = true;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.loading = false;
      this.cdr.detectChanges();
    }, 500);
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
    this.cdr.detectChanges();
  }

  onButtonClick(buttonName: string): void {
    alert(`You clicked the ${buttonName} button!`);
  }

  onCountryChange(country: any): void {
    this.selectedCountry = country;
    this.selectedCity = null;

    if (country) {
      this.cities = this.citiesByCountry[country] || [];
    } else {
      this.cities = [];
    }

    this.cdr.detectChanges();
  }

  initializeLocationTree(): void {
    this.locationTreeData = [
      {
        label: 'USA',
        value: 'usa',
        expanded: false,
        cities: [
          { label: 'New York', value: 'usa-new-york' },
          { label: 'Los Angeles', value: 'usa-los-angeles' },
          { label: 'Chicago', value: 'usa-chicago' },
          { label: 'Houston', value: 'usa-houston' }
        ]
      },
      {
        label: 'Canada',
        value: 'canada',
        expanded: false,
        cities: [
          { label: 'Toronto', value: 'canada-toronto' },
          { label: 'Vancouver', value: 'canada-vancouver' },
          { label: 'Montreal', value: 'canada-montreal' },
          { label: 'Calgary', value: 'canada-calgary' }
        ]
      },
      {
        label: 'UK',
        value: 'uk',
        expanded: false,
        cities: [
          { label: 'London', value: 'uk-london' },
          { label: 'Manchester', value: 'uk-manchester' },
          { label: 'Birmingham', value: 'uk-birmingham' },
          { label: 'Edinburgh', value: 'uk-edinburgh' }
        ]
      },
      {
        label: 'Germany',
        value: 'germany',
        expanded: false,
        cities: [
          { label: 'Berlin', value: 'germany-berlin' },
          { label: 'Munich', value: 'germany-munich' },
          { label: 'Hamburg', value: 'germany-hamburg' },
          { label: 'Frankfurt', value: 'germany-frankfurt' }
        ]
      },
      {
        label: 'France',
        value: 'france',
        expanded: false,
        cities: [
          { label: 'Paris', value: 'france-paris' },
          { label: 'Lyon', value: 'france-lyon' },
          { label: 'Marseille', value: 'france-marseille' },
          { label: 'Nice', value: 'france-nice' }
        ]
      }
    ];

    this.filteredLocationData = [...this.locationTreeData];

    this.locationOptionsForDropdown = this.locationTreeData.map(country => ({
      label: country.label,
      value: country.value,
      expanded: false,
      children: country.cities.map((city: any) => ({
        label: city.label,
        value: city.value,
        parent: country.label
      }))
    }));
  }

  toggleLocationExpand(location: any): void {
    location.expanded = !location.expanded;
    location.icon = location.expanded ? 'fas fa-minus' : 'fas fa-plus';
    this.cdr.detectChanges();
  }

  onLocationSelect(item: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (item.children) {
      this.toggleLocationExpand(item);
    } else {
      this.selectedCity = item.value;
      this.cdr.detectChanges();
    }
  }

  toggleLocationDropdown(): void {
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
    this.cdr.detectChanges();
  }

  getSelectedLocationLabel(): string {
    return this.selectedLocation.label || '';
  }

  selectCity(city: any, country: any): void {
    this.selectedLocation = {
      country: country.value,
      city: city.value,
      label: `${country.label} - ${city.label}`
    };
    this.selectedCity = city.value;
    this.isLocationDropdownOpen = false;
    this.cdr.detectChanges();
  }

  toggleCountry(country: any): void {
    country.expanded = !country.expanded;
    this.cdr.detectChanges();
  }

  selectLocation(city: any, country: any): void {
    this.selectedLocation = {
      country: country.value,
      city: city.value,
      label: `${country.label} - ${city.label}`,
      value: city.value
    };
    this.isLocationDropdownOpen = false;
    this.locationSearchText = '';
    this.filterLocations();
    this.cdr.detectChanges();
  }

  filterLocations(): void {
    if (!this.locationSearchText) {
      this.filteredLocationData = [...this.locationTreeData];
    } else {
      const searchLower = this.locationSearchText.toLowerCase();
      this.filteredLocationData = this.locationTreeData
        .map(country => {
          const filteredCities = country.cities.filter((city: any) =>
            city.label.toLowerCase().includes(searchLower)
          );

          if (country.label.toLowerCase().includes(searchLower) || filteredCities.length > 0) {
            return {
              ...country,
              cities: country.label.toLowerCase().includes(searchLower) ? country.cities : filteredCities,
              expanded: filteredCities.length > 0
            };
          }
          return null;
        })
        .filter(Boolean);
    }
    this.cdr.detectChanges();
  }

  handleConfirm(): void {
    alert('Action confirmed!');
    this.showConfirmDialog = false;
  }

  handleFormSave(): void {
    alert(`Form saved! Name: ${this.dialogFormData.name}, Email: ${this.dialogFormData.email}`);
    this.showFormDialog = false;
    this.dialogFormData = {
      name: '',
      email: '',
      role: null,
      description: ''
    };
  }

  getIconPath(iconName: string): string {
    const theme = this.themeService.isDarkMode() ? 'dark' : 'light';
    return `assets/icons/main/${theme}/ico-${iconName}.svg`;
  }

  onMapClick(coordinates: { lat: number; lng: number }): void {
    const message = this.translate.instant('Map clicked at') + `: ${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`;
    alert(message);
  }

  onMarkerClick(coordinates: { lat: number; lng: number }): void {
  }

  onCoordinatesChange(coordinates: { lat: number; lng: number }[]): void {
  }

  onLeafletMapClick(event: any): void {
    this.leafletClickedCoords = [event.lat, event.lng];
    this.cdr.markForCheck();
  }

  onLeafletMarkerClick(event: any): void {
  }

  onLeafletCoordinatesChange(coords: [number, number]): void {
    this.leafletClickedCoords = coords;
    this.cdr.markForCheck();
  }

  onLeafletMapMove(event: any): void {
    this.leafletMoveSubject.next(event);
    if (this.leafletDynamicPolygonsEnabled) {
      this.updateLeafletPolygonsCount();
    }
  }

  onOpenlayersMapMove(event: any): void {
    this.openlayersMoveSubject.next(event);
    if (this.openlayersDynamicPolygonsEnabled) {
      this.updateOpenlayersPolygonsCount();
    }
  }

  onMaplibreMapClick(event: any): void {
    this.maplibreClickedCoords = event.lngLat;
    this.cdr.markForCheck();
  }

  onMaplibreMarkerClick(marker: any): void {
  }

  onMaplibreMapMove(event: any): void {

    this.moveSubject.next(event);

    if (this.dynamicPolygonsEnabled) {
      this.updateVisiblePolygonsCount();
    }
  }

  onMaplibreMapLoad(map: any): void {
    if (this.maplibreMap) {
      this.maplibreMap.addTileLayer('satellite-demo',
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          opacity: 0.5,
          attribution: '© Esri'
        }
      );

      setTimeout(() => {
        const popup = new (window as any).maplibregl.Popup({
          closeButton: false,
          className: 'demo-popup'
        })
        .setLngLat([69.2401, 41.2995])
        .setHTML('<div style="padding: 10px; background: #2196F3; color: white; border-radius: 4px;"><strong>✅ Два тайловых слоя активны!</strong><br>1. OSM (базовый)<br>2. Esri Satellite (50% прозрачность)</div>')
        .addTo(map);

        setTimeout(() => popup.remove(), 10000);
      }, 2000);
    }

    this.initializeDynamicPolygonsSource(map);

    this.loadVectorDataExample();
  }

  private async loadVectorDataExample(): Promise<void> {
    if (!this.maplibreMap) return;

    const sampleGeoJSON = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [69.20, 41.30],
              [69.25, 41.30],
              [69.25, 41.32],
              [69.20, 41.32],
              [69.20, 41.30]
            ]]
          },
          properties: {
            name: 'District 1',
            type: 'residential',
            population: 50000
          }
        },
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [69.26, 41.28],
              [69.30, 41.28],
              [69.30, 41.31],
              [69.26, 41.31],
              [69.26, 41.28]
            ]]
          },
          properties: {
            name: 'District 2',
            type: 'commercial',
            population: 30000
          }
        }
      ]
    };

    const districtsWithColors = sampleGeoJSON.features.map((feature, index) => ({
      ...feature,
      properties: {
        ...feature.properties,
        color: feature.properties.type === 'residential'
          ? `rgba(76, 175, 80, ${0.4 + index * 0.1})`
          : `rgba(33, 150, 243, ${0.4 + index * 0.1})`,
        opacity: 0.8,
        strokeColor: feature.properties.type === 'residential' ? '#388E3C' : '#1565C0',
        strokeWidth: 3
      }
    }));

    if (this.maplibreMap) {
      this.maplibreMap.addSVGVectorLayer('districts-svg', districtsWithColors, {
        width: 2048,
        height: 2048,
        style: {
          blur: false,
          shadow: true
        }
      });
    }

    const svgVectorData = [
      {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [69.15, 41.25],
            [69.18, 41.25],
            [69.18, 41.27],
            [69.15, 41.27],
            [69.15, 41.25]
          ]]
        },
        properties: {
          name: 'Precipitation Area 1',
          color: '#2196F3',
          opacity: 0.4
        }
      },
      {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [69.22, 41.29],
            [69.26, 41.29],
            [69.26, 41.31],
            [69.22, 41.31],
            [69.22, 41.29]
          ]]
        },
        properties: {
          name: 'Precipitation Area 2',
          color: '#1976D2',
          opacity: 0.6
        }
      }
    ];

    this.maplibreMap.addSVGVectorLayer('custom-svg-layer', svgVectorData, {
      width: 2048,
      height: 2048
    });

  }

  addPolygonLayer(): void {
    if (!this.maplibreMap) return;

    const polygonData = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [69.23, 41.29],
          [69.25, 41.29],
          [69.25, 41.31],
          [69.23, 41.31],
          [69.23, 41.29]
        ]]
      }
    };

    this.maplibreMap.addGeoJSONLayer('polygon-layer', polygonData, {
      id: 'polygon-layer',
      type: 'fill',
      source: 'polygon-layer',
      paint: {
        'fill-color': '#FF5722',
        'fill-opacity': 0.5,
        'fill-outline-color': '#D32F2F'
      }
    });

    this.cdr.markForCheck();
  }

  addHeatmapLayer(): void {
    if (!this.maplibreMap) return;

    const heatmapData = {
      type: 'FeatureCollection',
      features: [
        { type: 'Feature', geometry: { type: 'Point', coordinates: [69.24, 41.30] } },
        { type: 'Feature', geometry: { type: 'Point', coordinates: [69.245, 41.298] } },
        { type: 'Feature', geometry: { type: 'Point', coordinates: [69.235, 41.302] } },
        { type: 'Feature', geometry: { type: 'Point', coordinates: [69.242, 41.295] } },
        { type: 'Feature', geometry: { type: 'Point', coordinates: [69.238, 41.299] } }
      ]
    };

    this.maplibreMap.addHeatmapLayer('heatmap-layer', heatmapData, {
      radius: 50,
      intensity: 1.5
    });

    this.cdr.markForCheck();
  }

  addLineLayer(): void {
    if (!this.maplibreMap) return;

    const lineData = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [69.22, 41.28],
          [69.24, 41.295],
          [69.26, 41.31],
          [69.28, 41.30]
        ]
      }
    };

    this.maplibreMap.addLineLayer('line-layer', lineData, {
      color: '#2196F3',
      width: 5
    });

    this.cdr.markForCheck();
  }

  addWeatherLayer(): void {
    if (!this.maplibreMap) return;

    this.maplibreMap.addWeatherLayer('precipitation', 0.6);

    this.cdr.markForCheck();
  }

  clearLayers(): void {
    if (!this.maplibreMap) return;

    this.maplibreMap.removeLayer('polygon-layer');
    this.maplibreMap.removeLayer('heatmap-layer');
    this.maplibreMap.removeLayer('line-layer');
    this.maplibreMap.removeLayer('weather-radar-layer');

    this.maplibreMap.removeSource('polygon-layer');
    this.maplibreMap.removeSource('heatmap-layer');
    this.maplibreMap.removeSource('line-layer');
    this.maplibreMap.removeSource('weather-radar');

    this.maplibreMap.removeSVGVectorLayer('districts-svg');
    this.maplibreMap.removeSVGVectorLayer('custom-svg-layer');

    this.cdr.markForCheck();
  }

  setMapStyle(style: 'light' | 'dark' | 'satellite'): void {
    if (!this.maplibreMap) return;
    this.maplibreMap.setMapStyle(style);
  }

  private satelliteDemoVisible = true;
  toggleSatelliteDemo(): void {
    if (!this.maplibreMap) return;

    this.satelliteDemoVisible = !this.satelliteDemoVisible;
    this.maplibreMap.setLayerVisibility('satellite-demo-layer', this.satelliteDemoVisible);

  }

  initializeDynamicPolygonsSource(map: any): void {
    map.addSource('dynamic-polygons', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    map.addLayer({
      id: 'dynamic-polygons-fill',
      type: 'fill',
      source: 'dynamic-polygons',
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.6
      }
    });

    map.addLayer({
      id: 'dynamic-polygons-outline',
      type: 'line',
      source: 'dynamic-polygons',
      paint: {
        'line-color': '#000',
        'line-width': 2
      }
    });
  }

  toggleDynamicPolygons(): void {
    this.dynamicPolygonsEnabled = !this.dynamicPolygonsEnabled;

    if (this.dynamicPolygonsEnabled) {
      this.loadPolygonsForCurrentView();
    } else {
      this.clearDynamicPolygons();
    }
  }

  private async loadPolygonsForCurrentView(): Promise<void> {
    if (!this.maplibreMap) return;

    const map = this.maplibreMap.getMap();
    if (!map) return;

    try {
      const bounds = map.getBounds();
      const bbox = {
        west: bounds.getWest(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        north: bounds.getNorth()
      };

      const boundKey = `${Math.floor(bbox.west * 100)}_${Math.floor(bbox.south * 100)}_${Math.floor(bbox.east * 100)}_${Math.floor(bbox.north * 100)}`;

      if (this.loadedBounds.has(boundKey)) {
        return;
      }

      const newPolygons = this.generatePolygonsForBbox(bbox);

      newPolygons.features.forEach((feature: any) => {
        if (!this.loadedPolygons.has(feature.id)) {
          this.loadedPolygons.set(feature.id, feature);
        }
      });

      this.loadedBounds.add(boundKey);

      this.updateDynamicPolygonsOnMap();


    } catch (error) {
      console.error('Ошибка загрузки полигонов:', error);
    }
  }

  private generatePolygonsForBbox(bbox: any): any {
    const features = [];
    const gridSize = 0.02;

    for (let lng = bbox.west; lng < bbox.east; lng += gridSize * 3) {
      for (let lat = bbox.south; lat < bbox.north; lat += gridSize * 3) {
        if (Math.random() > 0.3) continue;

        const feature = {
          type: 'Feature',
          id: `polygon_${lng.toFixed(4)}_${lat.toFixed(4)}`,
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [lng, lat],
              [lng + gridSize * 1.5, lat],
              [lng + gridSize * 1.5, lat + gridSize * 1.5],
              [lng, lat + gridSize * 1.5],
              [lng, lat]
            ]]
          },
          properties: {
            color: this.getRandomColor(),
            name: `Участок ${Math.floor(Math.random() * 1000)}`,
            area: Math.floor(Math.random() * 10000) + ' м²',
            type: ['residential', 'commercial', 'industrial'][Math.floor(Math.random() * 3)]
          }
        };
        features.push(feature);
      }
    }

    return {
      type: 'FeatureCollection',
      features
    };
  }

  private updateDynamicPolygonsOnMap(): void {
    if (!this.maplibreMap) return;

    const map = this.maplibreMap.getMap();
    if (!map) return;

    const source = map.getSource('dynamic-polygons');
    if (source) {
      const features = Array.from(this.loadedPolygons.values());
      source.setData({
        type: 'FeatureCollection',
        features
      });

      this.polygonsCount = features.length;
      this.cdr.markForCheck();
    }
  }

  private updateVisiblePolygonsCount(): void {
    if (!this.maplibreMap) return;

    const map = this.maplibreMap.getMap();
    if (!map) return;

    const bounds = map.getBounds();
    let visibleCount = 0;

    this.loadedPolygons.forEach((feature) => {
      const coords = feature.geometry.coordinates[0];
      for (const coord of coords) {
        if (bounds.contains(coord)) {
          visibleCount++;
          break;
        }
      }
    });
  }

  private clearDynamicPolygons(): void {
    this.loadedPolygons.clear();
    this.loadedBounds.clear();
    this.polygonsCount = 0;
    this.updateDynamicPolygonsOnMap();
  }

  clearAllDynamicPolygons(): void {

    this.loadedPolygons.clear();
    this.loadedBounds.clear();
    this.polygonsCount = 0;

    this.updateDynamicPolygonsOnMap();

    this.cdr.markForCheck();
  }

  private getRandomColor(): string {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#48C9B0'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  private generateRandomPolygons(bounds: any, count: number): any[] {
    const polygons = [];
    const latRange = bounds.north - bounds.south;
    const lngRange = bounds.east - bounds.west;

    for (let i = 0; i < count; i++) {
      const size = 0.005 + Math.random() * 0.01;
      const lat = bounds.south + Math.random() * (latRange - size);
      const lng = bounds.west + Math.random() * (lngRange - size);

      const coordinates = [
        [lat, lng],
        [lat + size, lng],
        [lat + size, lng + size],
        [lat, lng + size],
        [lat, lng]
      ];

      polygons.push({
        coordinates,
        color: this.getRandomColor(),
        id: `polygon_${Date.now()}_${i}`
      });
    }

    return polygons;
  }

  toggleLeafletSatellite(): void {
    if (this.leafletMap) {
      this.leafletMap.toggleSatelliteLayer();
      this.leafletSatelliteVisible = !this.leafletSatelliteVisible;
    }
  }

  updateLeafletSatelliteOpacity(): void {
    if (this.leafletMap) {
      this.leafletMap.setSatelliteOpacity(this.leafletSatelliteOpacity);
    }
  }

  toggleOpenlayersSatellite(): void {
    if (this.openlayersMap) {
      this.openlayersMap.toggleSatelliteLayer();
      this.openlayersSatelliteVisible = !this.openlayersSatelliteVisible;
    }
  }

  updateOpenlayersSatelliteOpacity(): void {
    if (this.openlayersMap) {
      this.openlayersMap.setSatelliteOpacity(this.openlayersSatelliteOpacity);
    }
  }

  toggleLeafletDynamicPolygons(): void {
    this.leafletDynamicPolygonsEnabled = !this.leafletDynamicPolygonsEnabled;

    if (!this.leafletDynamicPolygonsEnabled) {
      this.clearAllLeafletDynamicPolygons();
    } else {
      this.loadPolygonsForLeafletView();
    }
    this.cdr.markForCheck();
  }

  private async loadPolygonsForLeafletView(): Promise<void> {
    if (!this.leafletMap) return;

    const bounds = this.leafletMap.getMapBounds();
    if (!bounds) return;

    const boundKey = `${Math.floor(bounds.west * 100)}_${Math.floor(bounds.south * 100)}_${Math.floor(bounds.east * 100)}_${Math.floor(bounds.north * 100)}`;

    if (this.leafletLoadedBounds.has(boundKey)) {
      return;
    }

    const polygonsToAdd = this.generateRandomPolygons(bounds, 30);

    polygonsToAdd.forEach((polygon, index) => {
      const id = `leaflet_polygon_${boundKey}_${index}`;
      this.leafletMap!.addDynamicPolygon(polygon.coordinates, {
        color: polygon.color,
        fillColor: polygon.color,
        popup: `Полигон ${id}`
      });
      this.leafletLoadedPolygons.set(id, polygon);
    });

    this.leafletLoadedBounds.add(boundKey);
    this.updateLeafletPolygonsCount();
  }

  private updateLeafletPolygonsCount(): void {
    if (this.leafletMap) {
      this.leafletPolygonsCount = this.leafletMap.getDynamicPolygonsCount();
      this.cdr.markForCheck();
    }
  }

  clearAllLeafletDynamicPolygons(): void {
    if (this.leafletMap) {
      this.leafletMap.clearDynamicPolygons();
      this.leafletLoadedPolygons.clear();
      this.leafletLoadedBounds.clear();
      this.leafletPolygonsCount = 0;
      this.cdr.markForCheck();
    }
  }

  toggleOpenlayersDynamicPolygons(): void {
    this.openlayersDynamicPolygonsEnabled = !this.openlayersDynamicPolygonsEnabled;

    if (!this.openlayersDynamicPolygonsEnabled) {
      this.clearAllOpenlayersDynamicPolygons();
    } else {
      this.loadPolygonsForOpenlayersView();
    }
    this.cdr.markForCheck();
  }

  private async loadPolygonsForOpenlayersView(): Promise<void> {
    if (!this.openlayersMap) return;

    const bounds = this.openlayersMap.getMapBounds();
    if (!bounds) return;

    const boundKey = `${Math.floor(bounds.west * 100)}_${Math.floor(bounds.south * 100)}_${Math.floor(bounds.east * 100)}_${Math.floor(bounds.north * 100)}`;

    if (this.openlayersLoadedBounds.has(boundKey)) {
      return;
    }

    const polygonsToAdd = this.generateRandomPolygons(bounds, 30);

    polygonsToAdd.forEach((polygon, index) => {
      const id = `openlayers_polygon_${boundKey}_${index}`;
      this.openlayersMap!.addDynamicPolygon(polygon.coordinates, {
        color: polygon.color,
        fillColor: polygon.color,
        popup: `Полигон ${id}`
      });
      this.openlayersLoadedPolygons.set(id, polygon);
    });

    this.openlayersLoadedBounds.add(boundKey);
    this.updateOpenlayersPolygonsCount();
  }

  private updateOpenlayersPolygonsCount(): void {
    if (this.openlayersMap) {
      this.openlayersPolygonsCount = this.openlayersMap.getDynamicPolygonsCount();
      this.cdr.markForCheck();
    }
  }

  clearAllOpenlayersDynamicPolygons(): void {
    if (this.openlayersMap) {
      this.openlayersMap.clearDynamicPolygons();
      this.openlayersLoadedPolygons.clear();
      this.openlayersLoadedBounds.clear();
      this.openlayersPolygonsCount = 0;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDemoMapLoad(map: any): void {

    if (!this.demoMap) {
      console.error('Demo map component not found!');
      return;
    }

    try {
      this.demoMap.addTileLayer('satellite-overlay',
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          opacity: 0.5,
          attribution: '© Esri'
        }
      );
      console.log('Satellite layer added successfully!');

      setTimeout(() => {
        const mapInstance = this.demoMap?.getMap();
        if (mapInstance) {
          const layers = mapInstance.getStyle().layers;
          console.log('Current layers:', layers.map((l: any) => l.id));
        }
      }, 1000);

    } catch (error) {
      console.error('Error adding satellite layer:', error);
    }

    this.cdr.markForCheck();
  }

  updateDemoSatelliteOpacity(): void {
    if (this.demoMap) {
      const map = this.demoMap.getMap();
      if (map && map.getLayer('satellite-overlay-layer')) {
        map.setPaintProperty('satellite-overlay-layer', 'raster-opacity', this.demoSatelliteOpacity / 100);
      }
    }
  }

  toggleDemoLayers(): void {
    if (!this.demoMap) return;
    const map = this.demoMap.getMap();
    if (!map) return;

    if (this.demoLayersSwapped) {
      map.moveLayer('satellite-overlay-layer', 'osm-layer');
    } else {
      map.moveLayer('osm-layer', 'satellite-overlay-layer');
    }

    this.demoLayersSwapped = !this.demoLayersSwapped;
    this.cdr.markForCheck();
  }

  toggleSatelliteVisibility(): void {
    if (!this.demoMap) return;

    this.satelliteLayerVisible = !this.satelliteLayerVisible;
    this.demoMap.setLayerVisibility('satellite-overlay-layer', this.satelliteLayerVisible);
    this.cdr.markForCheck();
  }
}
